import styled, { css } from 'styled-components';
import { renderBreakPoints, BreakPoint } from '../Grid';

function Text({ children, heading1, heading2, heading3, heading4, heading5, p, ...props }) {
    if (heading1) return <H1 {...props}>{children}</H1>;

    if (heading2) return <H2 {...props}>{children}</H2>;

    if (heading3) return <H3 {...props}>{children}</H3>;

    if (heading4) return <H4 {...props}>{children}</H4>;

    if (heading5) return <H5 {...props}>{children}</H5>;

    if (p) return <Paragraph {...props}>{children}</Paragraph>;

    return <TextStyle {...props}>{children}</TextStyle>;
}

const headingStyle = props => css`
    display: inline;
    font-family: 'Coluna Rounded';
    font-weight: 700;
    margin: 0;
    padding: 0;

    ${props.color &&
    css`
        color: ${props.color};
    `}
    ${props.upper &&
    css`
        text-transform: uppercase;
    `}
    ${props.letter &&
    css`
        letter-spacing: ${props.letter};
    `}

    ${props =>
        props.bg &&
        css`
            background-color: ${props.bg};
            padding: 8px;
            padding-top: 12px;
            padding-left: 0px;
            box-shadow: -8px 0 0 ${props.bg};
            position: relative;
            left: 8px;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
        `}

        ${props =>
        props.hover &&
        css`
            :hover {
                opacity: 0.7;
                transition: all ease-in-out 0.2s;
            }
        `}
`;

const fontStyle = (size, lineHeight) => css`
    ${BreakPoint(
        'sm',
        css`
            font-size: ${size / 10}rem;
            line-height: ${lineHeight / 10}rem;
        `
    )}

    font-size: 1.6rem;
    line-height: 2.1rem;
`;

const TextStyle = styled.span`
    display: inline;
    font-family: 'Open Sans Hebrew', sans-serif;
    cursor: ${({ cursor }) => cursor ?? 'inherit'};

    ${props =>
        props.bg &&
        css`
            background-color: ${props.bg};
            padding: 9px 9px 9px 8px;
            position: relative;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
        `}

    ${props => {
        let sizes = props.size ? props.size.split(' ') : ['14'];
        return renderBreakPoints(k => {
            if (!sizes[k]) return;

            return css`
                font-size: ${parseInt(sizes[k], 10) / 10}rem;
                line-height: ${(parseInt(sizes[k], 10) / 10) * 1.2}rem;
            `;
        });
    }}

    ${props => {
        if (!props.title) return;

        let sizes = props.size ? props.size.split(' ') : ['14'];
        return renderBreakPoints(k => {
            if (!sizes[k]) return;

            return css`
                line-height: ${sizes[k] ? sizes[k] / 10 : 1.4}rem;
                margin-bottom: -${sizes[k] / 10}px;
                top: ${sizes[k] / 10}px;
                margin-top: -${sizes[k] / 10}px;
            `;
        }, props.mainNews);
    }}

    ${props => {
        if (!props.lineHeight) return;

        let lineHeight = props.lineHeight.split(' ');
        return renderBreakPoints(k => {
            if (!lineHeight[k]) return;

            return css`
                line-height: ${parseInt(lineHeight[k], 10) / 10}rem;
            `;
        });
    }}
    
    letter-spacing: -0.02rem;
    ${props =>
        props.color &&
        css`
            color: ${props.color};
        `}

    ${props => css`
        ${props.highlight &&
        css`
            font-weight: 700;
        `}
        ${props.quote && fontStyle(16, 24)}
        ${props.body1 && fontStyle(16, 24)}
        ${props.body2 && fontStyle(16, 21.79)}
        ${props.body3 && fontStyle(12, 16)} 

        ${props.underline &&
        css`
            text-decoration: underline;
        `} 
        ${props.upper &&
        css`
            text-transform: uppercase;
        `} 
        
        ${props.title &&
        css`
            font-family: 'Coluna Rounded';
            position: relative;
        `}
        ${props.letter &&
        css`
            letter-spacing: ${props.letter};
        `}
    `}

    ${props =>
        props.hover &&
        css`
            :hover {
                opacity: 0.7;
                transition: all ease-in-out 0.2s;
            }
        `}
`;

const Paragraph = styled.span`
    font-size: 1.6rem;
    line-height: 24px;
    letter-spacing: -0.02rem;
`;
const H1 = styled.h1`
    ${props => headingStyle(props)}
    font-size: 9.6rem;
    line-height: 8.8rem;
`;
const H2 = styled.h2`
    ${props => headingStyle(props)}
    font-size: 7.2rem;
    line-height: 6.4rem;
`;
const H3 = styled.h3`
    ${props => headingStyle(props)}
    font-size: 3.2rem;
    line-height: 3rem;

    ${BreakPoint(
        'sm',
        css`
            font-size: 5.6rem;
            line-height: 4.8rem;
        `
    )}
`;
const H4 = styled.h4`
    ${props => headingStyle(props)}
    font-size: 3.2rem;
    line-height: 3rem;

    ${BreakPoint(
        'sm',
        css`
            font-size: 4rem;
            line-height: 3.2rem;
        `
    )}
`;
const H5 = styled.h5`
    ${props => headingStyle(props)}
    font-size: ${({ size }) => (size ? size / 10 : 3.2)}rem;
    line-height: 3rem;
`;

export default Text;
